import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Eye, EyeOff, Lock, Unlock, ChevronUp, ChevronDown, Trash2, Tag, Link, Unlink, Layers, ChevronRight, FileImage } from 'lucide-react';
import { categoryFieldAPI } from '../../services/api';
import AttachFieldDialog from './AttachFieldDialog';

const ItemsListSidebar = ({ 
  isOpen, 
  onClose, 
  items = [], 
  selectedElement, 
  onElementSelect, 
  onElementToggleVisibility,
  onElementToggleLock,
  onElementMoveUp,
  onElementMoveDown,
  onElementDelete,
  onGroupChildSelect,
  template_content,
  onTemplateUpdate,
  template_category_id
}) => {

  console.log('🎯 ItemsListSidebar component rendered with props:', {
    isOpen,
    items,
    selectedElement,
    onElementSelect,
    onElementToggleVisibility,
    onElementToggleLock,
    onElementMoveUp,
    onElementMoveDown,
    onElementDelete,
    template_content,
    onTemplateUpdate,
    template_category_id
  });
  
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, itemId: null, itemName: '' });
  const [attachFieldDialog, setAttachFieldDialog] = useState({ isOpen: false, itemId: null, itemType: null });
  const [expandedGroups, setExpandedGroups] = useState({});
  const [quickDeleteMode, setQuickDeleteMode] = useState(false);
  const [tooltipItem, setTooltipItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hoverTimerRef = useRef(null);

  // Toggle group expansion in the sidebar
  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Long hover tooltip handlers
  const handleItemMouseEnter = (item, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    hoverTimerRef.current = setTimeout(() => {
      setTooltipPosition({ x: rect.right + 8, y: rect.top });
      setTooltipItem(item);
    }, 600); // 600ms long hover
  };

  const handleItemMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setTooltipItem(null);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // Handle clicking a child entry inside a group
  const handleGroupChildClick = (groupId, childId) => {
    if (onGroupChildSelect) {
      onGroupChildSelect(groupId, childId);
    } else {
      // Fallback: just select the child
      onElementSelect(childId);
    }
  };

  // Auto-expand groups when a child inside them is selected
  useEffect(() => {
    if (selectedElement) {
      const parentGroup = items.find(
        item => item.type === 'group' && item.children?.some(child => child.id === selectedElement)
      );
      if (parentGroup && !expandedGroups[parentGroup.id]) {
        setExpandedGroups(prev => ({ ...prev, [parentGroup.id]: true }));
      }
    }
  }, [selectedElement, items]);
  


  const attachField = (elementId, fieldId) => {
    if (!onTemplateUpdate) return;

    const updatedPages = template_content.pages.map(page => ({
      ...page,
      items: page.items.map(item => {
        // Check if this is the item we're looking for
        if (item.id === elementId) {
          return { ...item, categoryFieldId: fieldId }
        }

        // Check if this item is a PowerClip container with nested items
        if (item.isPowerClipContainer && item.powerClipContents) {
          const updatedContents = item.powerClipContents.map(nestedItem =>
            nestedItem.id === elementId
              ? { ...nestedItem, categoryFieldId: fieldId }
              : nestedItem
          )

          // Only update if we found the item in nested contents
          if (updatedContents.some((c, i) => c !== item.powerClipContents[i])) {
            return { ...item, powerClipContents: updatedContents }
          }
        }

        // Check if this item is a group with children
        if (item.type === 'group' && item.children) {
          const updatedChildren = item.children.map(child =>
            child.id === elementId
              ? { ...child, categoryFieldId: fieldId }
              : child
          )

          if (updatedChildren.some((c, i) => c !== item.children[i])) {
            return { ...item, children: updatedChildren }
          }
        }

        return item
      })
    }));

    onTemplateUpdate({
      ...template_content,
      pages: updatedPages
    });
  };

  const removeField = (elementId) => {
    if (!onTemplateUpdate) return;

    const updatedPages = template_content.pages.map(page => ({
      ...page,
      items: page.items.map(item => {
        // Check if this is the item we're looking for
        if (item.id === elementId) {
          return { ...item, categoryFieldId: null, categoryField: null }
        }

        // Check if this item is a PowerClip container with nested items
        if (item.isPowerClipContainer && item.powerClipContents) {
          const updatedContents = item.powerClipContents.map(nestedItem =>
            nestedItem.id === elementId
              ? { ...nestedItem, categoryFieldId: null, categoryField: null }
              : nestedItem
          )

          // Only update if we found the item in nested contents
          if (updatedContents.some((c, i) => c !== item.powerClipContents[i])) {
            return { ...item, powerClipContents: updatedContents }
          }
        }

        // Check if this item is a group with children
        if (item.type === 'group' && item.children) {
          const updatedChildren = item.children.map(child =>
            child.id === elementId
              ? { ...child, categoryFieldId: null, categoryField: null }
              : child
          )

          if (updatedChildren.some((c, i) => c !== item.children[i])) {
            return { ...item, children: updatedChildren }
          }
        }

        return item
      })
    }));

    onTemplateUpdate({
      ...template_content,
      pages: updatedPages
    });
  };

  const canAttachField = (item) => {
    return item.type === 'text' || item.type === 'image' || item.type === 'tts' || item.type === 'video' || item.type === 'audio' || item.type === 'sound-effect';
  };

  const getAttachedField = (item) => {
    return item.categoryField || null;
  };

  // Dialog handlers
  const handleOpenAttachFieldDialog = (item) => {
    setAttachFieldDialog({
      isOpen: true,
      itemId: item.id,
      itemType: item.type
    });
  };

  const handleCloseAttachFieldDialog = () => {
    setAttachFieldDialog({ isOpen: false, itemId: null, itemType: null });
  };

  const handleAttachFieldFromDialog = (itemId, fieldId, field, isGlobal) => {
    console.log('🔗 Attaching field from dialog:', { itemId, fieldId, field, isGlobal });

    if (!onTemplateUpdate) {
      console.error('❌ onTemplateUpdate is not available');
      return;
    }

    const updatedPages = template_content.pages.map(page => ({
      ...page,
      items: page.items.map(item => {
        // Check if this is the item we're looking for
        if (item.id === itemId) {
          return {
            ...item,
            categoryFieldId: fieldId,
            categoryField: field // Store the full field object for display
          };
        }

        // Check if this item is a PowerClip container with nested items
        if (item.isPowerClipContainer && item.powerClipContents) {
          const updatedContents = item.powerClipContents.map(nestedItem =>
            nestedItem.id === itemId
              ? {
                  ...nestedItem,
                  categoryFieldId: fieldId,
                  categoryField: field // Store the full field object for display
                }
              : nestedItem
          );

          // Only update if we found the item in nested contents
          if (updatedContents.some((c, i) => c !== item.powerClipContents[i])) {
            return { ...item, powerClipContents: updatedContents };
          }
        }

        // Check if this item is a group with children
        if (item.type === 'group' && item.children) {
          const updatedChildren = item.children.map(child =>
            child.id === itemId
              ? {
                  ...child,
                  categoryFieldId: fieldId,
                  categoryField: field
                }
              : child
          );

          if (updatedChildren.some((c, i) => c !== item.children[i])) {
            return { ...item, children: updatedChildren };
          }
        }

        return item;
      })
    }));

    console.log('📝 Updating template with field attachment:', updatedPages);

    onTemplateUpdate({
      ...template_content,
      pages: updatedPages
    });
  };

  // Delete confirmation dialog handlers
  const handleDeleteClick = (item) => {
    if (quickDeleteMode) {
      // Quick delete: skip confirmation dialog
      if (onElementDelete) {
        onElementDelete(item.id);
      }
      return;
    }
    setDeleteConfirmDialog({
      open: true,
      itemId: item.id,
      itemName: getElementName(item)
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmDialog.itemId && onElementDelete) {
      onElementDelete(deleteConfirmDialog.itemId);
    }
    setDeleteConfirmDialog({ open: false, itemId: null, itemName: '' });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ open: false, itemId: null, itemName: '' });
  };

  // Handle keyboard shortcuts for delete dialog
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (deleteConfirmDialog.open) {
        if (e.key === 'Escape') {
          handleDeleteCancel();
        } else if (e.key === 'Enter') {
          handleDeleteConfirm();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleteConfirmDialog.open]);

  // Drag and drop handlers
  const handleDragStart = (e, item, index) => {
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (!draggedItem || draggedItem.index === dropIndex) {
      return;
    }

    // Get all items and reorder them
    const draggedItemData = sortedItems[draggedItem.index];
    const reorderedItems = [...sortedItems];
    
    // Remove dragged item from its current position
    reorderedItems.splice(draggedItem.index, 1);
    
    // Insert it at the new position
    reorderedItems.splice(dropIndex, 0, draggedItemData);
    
    // Reassign layer values as integers starting from the highest
    const layerUpdates = {};
    reorderedItems.forEach((item, index) => {
      const newLayer = reorderedItems.length - index; // Highest layer for first item
      layerUpdates[item.id] = newLayer;
    });

    // Update the template content with new layer values
    if (onTemplateUpdate && template_content) {
      const updatedPages = template_content.pages.map(page => ({
        ...page,
        items: page.items.map(item => 
          layerUpdates[item.id] !== undefined 
            ? { ...item, layer: layerUpdates[item.id] }
            : item
        )
      }));

      onTemplateUpdate({
        ...template_content,
        pages: updatedPages
      });
    }

    setDraggedItem(null);
  };

  // Sort items by layer (highest layer first)
  const sortedItems = [...items].sort((a, b) => (b.layer || 0) - (a.layer || 0));

  // Get element type icon
  const getElementIcon = (item) => {
    switch (item.type) {
      case 'text':
        return '📝';
      case 'tts':
        return '🎤';
      case 'wordart':
        return '🎨';
      case 'image':
        return '🖼️';
      case 'video':
        return '🎥';
      case 'audio':
        return '🎵';
      case 'sound-effect':
        return '🔊';
      case 'audio-effect':
        return '🎛️';
      case 'shape':
        return '🔷';
      case 'graphic':
        return '⭐';
      case 'svg-graphic':
        return '🎯';
      case 'group':
        return '📁';
      default:
        return '📄';
    }
  };

  // Get thumbnail for item
  const getThumbnail = (item) => {
    switch (item.type) {
      case 'image':
        return item.src || item.imageUrl || null;
      case 'video':
        return item.thumbnail || item.poster || null; // Video thumbnail or poster
      case 'shape':
        return null; // Will render shape preview
      case 'text':
        return null; // Will render text preview
      default:
        return null;
    }
  };

  // Render item thumbnail
  const renderThumbnail = (item) => {
    const thumbnail = getThumbnail(item);

    if (item.type === 'image' && thumbnail) {
      return (
        <div className="w-10 h-10 rounded border overflow-hidden bg-gray-100 flex-shrink-0">
          <img
            src={thumbnail}
            alt="thumbnail"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="w-full h-full bg-gray-200 items-center justify-center text-gray-500 text-xs hidden">
            🖼️
          </div>
        </div>
      );
    }

    if (item.type === 'video' && thumbnail) {
      return (
        <div className="w-10 h-10 rounded border overflow-hidden bg-gray-100 flex-shrink-0 relative">
          <img
            src={thumbnail}
            alt="video thumbnail"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="w-full h-full bg-gray-200 items-center justify-center text-gray-500 text-xs hidden">
            🎥
          </div>
          {/* Video play icon overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-l-[6px] border-l-white border-y-[4px] border-y-transparent ml-0.5"></div>
            </div>
          </div>
        </div>
      );
    }

    // Inline SVG preview (type:"svg" with svgContent, or legacy graphic/svg-graphic with svgData)
    if (item.type === 'svg' || item.type === 'svg-graphic' || item.type === 'graphic') {
      const rawSvg = item.svgContent || item.svgData || item.originalSvgData;
      if (rawSvg && /<svg[\s>]/i.test(rawSvg)) {
        // Scale the SVG to fit the 40x40 thumbnail box while preserving aspect.
        const preview = rawSvg.replace(/<svg([^>]*)>/i, (_m, attrs) => {
          let a = attrs
            .replace(/\swidth="[^"]*"/i, '')
            .replace(/\sheight="[^"]*"/i, '')
            .replace(/\spreserveAspectRatio="[^"]*"/i, '');
          return `<svg${a} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style="display:block">`;
        });
        return (
          <div className="w-10 h-10 rounded border bg-white flex items-center justify-center flex-shrink-0 p-0.5 overflow-hidden">
            <div
              className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
              dangerouslySetInnerHTML={{ __html: preview }}
            />
          </div>
        );
      }
      // No markup available — fall back to a generic icon.
      return (
        <div className="w-10 h-10 rounded border bg-gray-50 flex items-center justify-center flex-shrink-0 text-gray-400">
          <FileImage className="w-5 h-5" />
        </div>
      );
    }

    if (item.type === 'shape') {
      const shapeType = item.shapeType || 'rectangle';
      const fill = item.fill || '#3B82F6';
      return (
        <div className="w-10 h-10 rounded border bg-gray-50 flex items-center justify-center flex-shrink-0">
          <div
            className="w-6 h-6 rounded-sm"
            style={{
              backgroundColor: fill,
              borderRadius: shapeType === 'circle' ? '50%' :
                           shapeType === 'triangle' ? '0' : '2px'
            }}
          />
        </div>
      );
    }

    if (item.type === 'text') {
      const content = item.content || 'Text';
      return (
        <div className="w-10 h-10 rounded border bg-gray-50 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-medium text-blue-600" title={content}>
            📝
          </span>
        </div>
      );
    }

    // Group icon thumbnail
    if (item.type === 'group') {
      return (
        <div className="w-10 h-10 rounded border bg-indigo-50 flex items-center justify-center flex-shrink-0">
          <Layers className="w-5 h-5 text-indigo-600" />
        </div>
      );
    }

    // Default icon thumbnail
    return (
      <div className="w-10 h-10 rounded border bg-gray-100 flex items-center justify-center text-lg flex-shrink-0">
        {getElementIcon(item)}
      </div>
    );
  };

  // Get element display name
  const getElementName = (item) => {
    switch (item.type) {
      case 'text':
        if (item.content) {
          const displayText = item.content.length > 10 ? item.content.substring(0, 10) + '...' : item.content;
          return displayText;
        }
        return 'Text Element';
      case 'tts':
        if (item.text) {
          const displayText = item.text.length > 10 ? item.text.substring(0, 10) + '...' : item.text;
          return `TTS: ${displayText}`;
        }
        return 'TTS Element';
      case 'wordart':
        return item.content ? `WordArt: ${item.content.substring(0, 15)}${item.content.length > 15 ? '...' : ''}` : 'WordArt Element';
      case 'image':
        return item.alt || 'Image';
      case 'video':
        return item.alt || item.name || 'Video';
      case 'audio':
        return item.name || 'Audio';
      case 'sound-effect':
        return item.name || 'Sound Effect';
      case 'audio-effect':
        return item.name || 'Audio Effect';
      case 'shape':
        return `${item.shapeType || 'Shape'}`.charAt(0).toUpperCase() + `${item.shapeType || 'Shape'}`.slice(1);
      case 'graphic':
        return 'Graphic Element';
      case 'svg-graphic':
        return item.name || 'SVG Graphic';
      case 'svg':
        return item.name || 'SVG';
      case 'group':
        return 'Group';
      default:
        return 'Element';
    }
  };

  // Format time in seconds to readable format (e.g., "0s", "5.5s", "1m 30s")
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '0s';
    
    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Check if template is video type
  const isVideoTemplate = (template_content?.type || template_content?.mediaType) === 'video';

  if (!isOpen) return null;

  return (
    <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Layers</h2>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setQuickDeleteMode(!quickDeleteMode)}
            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
              quickDeleteMode
                ? 'bg-red-100 text-red-700 border border-red-300 hover:bg-red-200'
                : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
            }`}
            title={quickDeleteMode ? 'Quick Delete is ON - click delete buttons to remove without confirmation' : 'Enable Quick Delete to delete layers without confirmation'}
          >
            <Trash2 className="w-3 h-3 inline mr-1" />
            {quickDeleteMode ? 'Quick Delete ON' : 'Quick Delete'}
          </button>
          <button
            onClick={() => {
              if (!onTemplateUpdate || !template_content || items.length === 0) return
              // Sort items by layer ascending (lowest layer first) to maintain original order
              const sortedByLayer = [...items].sort((a, b) => (a.layer || 0) - (b.layer || 0))
              // Assign new layer values: 110, 120, 130, ...
              const layerUpdates = {}
              sortedByLayer.forEach((item, index) => {
                layerUpdates[item.id] = 110 + (index * 10)
              })
              // Update template_content
              const updatedPages = template_content.pages.map(page => ({
                ...page,
                items: page.items.map(item =>
                  layerUpdates[item.id] !== undefined
                    ? { ...item, layer: layerUpdates[item.id] }
                    : item
                )
              }))
              onTemplateUpdate({ ...template_content, pages: updatedPages })
            }}
            className="px-2 py-1 text-xs font-medium rounded transition-colors bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200"
            title="Reset all layer numbers to increments of 10 (110, 120, 130...) while keeping the original order"
          >
            <Layers className="w-3 h-3 inline mr-1" />
            Reset Layers
          </button>
          <span className="text-sm text-gray-500">
            {items.length} item{items.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {sortedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <div className="text-4xl mb-4">📄</div>
            <p className="text-center">No items on this page</p>
            <p className="text-sm text-center mt-1">Add elements to see them here</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {sortedItems.map((item, index) => (
              <React.Fragment key={item.id}>
                <div
                  draggable={true}
                  onClick={() => onElementSelect(item.id)}
                  onMouseEnter={(e) => handleItemMouseEnter(item, e)}
                  onMouseLeave={handleItemMouseLeave}
                  onDragStart={(e) => handleDragStart(e, item, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedElement === item.id
                      ? item.type === 'group' ? 'bg-indigo-100 border-indigo-400 shadow-md text-indigo-900' : 'bg-blue-100 border-blue-400 shadow-md text-blue-900'
                      : item.locked
                      ? 'bg-white border-red-500 hover:bg-gray-50'
                      : item.type === 'group'
                      ? 'bg-white border-indigo-200 hover:bg-indigo-50'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  } ${
                    dragOverIndex === index ? 'border-green-400 bg-green-50' : ''
                  }`}
                >
                {/* Element Icon and Info */}
                <div className="flex items-center flex-1 min-w-0">
                  {/* Group expand/collapse toggle */}
                  {item.type === 'group' && item.children && item.children.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroupExpanded(item.id);
                      }}
                      className="p-0.5 mr-1 hover:bg-indigo-100 rounded flex-shrink-0"
                      title={expandedGroups[item.id] ? 'Collapse group' : 'Expand group'}
                    >
                      <ChevronRight className={`w-4 h-4 text-indigo-500 transition-transform ${expandedGroups[item.id] ? 'rotate-90' : ''}`} />
                    </button>
                  )}
                  <div className="mr-3 flex-shrink-0">
                    {renderThumbnail(item)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {getElementName(item)}
                        {item.type === 'group' && item.children && (
                          <span className="text-xs text-indigo-500 ml-1">
                            ({item.children.length} item{item.children.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                        <span className="text-xs text-gray-500">
                          Layer {item.layer || 0}
                        </span>
                        {/* Time info for video templates - inline with layer number */}
                        {isVideoTemplate && item.time && (
                          <>
                            <span className="text-xs text-gray-400">
                              {formatTime(item.time.start)} - {formatTime((item.time.start || 0) + (item.time.duration || 0))}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Preview text for text items */}
                    {item.type === 'text' && item.content && (
                      <div 
                        className="text-sm text-gray-700 truncate mt-1"
                        style={{
                          fontFamily: item.fontFamily ? `"${item.fontFamily}", sans-serif` : 'inherit',
                          fontSize: item.fontSize ? `${Math.min(item.fontSize, 14)}px` : 'inherit',
                          fontWeight: item.fontWeight || 'normal',
                          color: item.color || '#000000'
                        }}
                      >
                        {item.content}
                      </div>
                    )}
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {Math.round(item.x)}, {Math.round(item.y)}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">
                        {Math.round(item.width)} × {Math.round(item.height)}
                      </span>
                      {item.rotation && (
                        <>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">
                            {Math.round(item.rotation)}°
                          </span>
                        </>
                      )}
                    </div>

                    {/* Category Field Info */}
                    {canAttachField(item) && (
                      <div className="mt-2">
                        {getAttachedField(item) ? (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded flex items-center">
                              <Tag className="w-3 h-3 mr-1" />
                              {getAttachedField(item).label}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeField(item.id);
                              }}
                              className="text-xs text-red-600 hover:text-red-800 p-1 rounded"
                              title="Remove field"
                            >
                              <Unlink className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAttachFieldDialog(item);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded bg-blue-50 flex items-center"
                              title="Attach field"
                            >
                              <Link className="w-3 h-3 mr-1" />
                              Attach Field
                            </button>

                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                  {/* Visibility Toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onElementToggleVisibility && onElementToggleVisibility(item.id);
                    }}
                    className="p-1 hover:bg-gray-200 rounded"
                    title={item.hidden ? 'Show element' : 'Hide element'}
                  >
                    {item.hidden ? (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-600" />
                    )}
                  </button>

                  {/* Lock Toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onElementToggleLock && onElementToggleLock(item.id);
                    }}
                    className="p-1 hover:bg-gray-200 rounded"
                    title={item.locked ? 'Unlock element' : 'Lock element'}
                  >
                    {item.locked ? (
                      <Lock className="w-4 h-4 text-red-500" />
                    ) : (
                      <Unlock className="w-4 h-4 text-gray-600" />
                    )}
                  </button>

                  {/* Layer Controls */}
                  <div className="flex flex-col">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onElementMoveUp && onElementMoveUp(item.id);
                      }}
                      className="p-0.5 hover:bg-gray-200 rounded"
                      title="Move layer up"
                      disabled={index === 0}
                    >
                      <ChevronUp className={`w-3 h-3 ${index === 0 ? 'text-gray-300' : 'text-gray-600'}`} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onElementMoveDown && onElementMoveDown(item.id);
                      }}
                      className="p-0.5 hover:bg-gray-200 rounded"
                      title="Move layer down"
                      disabled={index === sortedItems.length - 1}
                    >
                      <ChevronDown className={`w-3 h-3 ${index === sortedItems.length - 1 ? 'text-gray-300' : 'text-gray-600'}`} />
                    </button>
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(item);
                    }}
                    className="p-1 hover:bg-red-100 rounded text-red-600"
                    title="Delete element"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Group Children - rendered as indented sub-items */}
              {item.type === 'group' && expandedGroups[item.id] && item.children && item.children.length > 0 && (
                <div className="ml-6 space-y-1 mt-1 border-l-2 border-indigo-200 pl-2">
                  {[...item.children]
                    .sort((a, b) => (a.layer || 0) - (b.layer || 0))
                    .map((child) => (
                    <div
                      key={child.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGroupChildClick(item.id, child.id);
                      }}
                      className={`flex items-center p-2 rounded-lg border cursor-pointer transition-colors ${
                        selectedElement === child.id
                          ? 'bg-indigo-100 border-indigo-400 shadow-md text-indigo-900'
                          : 'bg-indigo-50 border-indigo-100 hover:bg-indigo-100'
                      }`}
                    >
                      {/* Child Icon and Info */}
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="mr-2 flex-shrink-0">
                          {renderThumbnail(child)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium text-gray-900 truncate flex items-center">
                              <span className="text-indigo-400 mr-1">↳</span>
                              {getElementName(child)}
                            </h3>
                            <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                              Layer {child.layer || 0}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-gray-500">
                              {Math.round(child.x)}, {Math.round(child.y)}
                            </span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">
                              {Math.round(child.width)} × {Math.round(child.height)}
                            </span>
                            {child.rotation ? (
                              <>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs text-gray-500">
                                  {Math.round(child.rotation)}°
                                </span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Child Action Buttons */}
                      <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(child);
                          }}
                          className="p-1 hover:bg-red-100 rounded text-red-600"
                          title="Delete child element"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </React.Fragment>
            ))}

            {/* PowerClip Nested Items Section */}
            {sortedItems.some(item => item.isPowerClipContainer && item.powerClipContents && item.powerClipContents.length > 0) && (
              <>
                {/* Divider */}
                <div className="my-4 border-t-2 border-gray-300 relative">
                  <span className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    PowerClip Contents
                  </span>
                </div>

                {/* Render nested items from PowerClip containers */}
                {sortedItems.map((item) => {
                  if (!item.isPowerClipContainer || !item.powerClipContents || item.powerClipContents.length === 0) {
                    return null
                  }

                  return (
                    <div key={`powerclip-${item.id}`} className="mb-4">
                      {/* Container Header */}
                      <div className="flex items-center space-x-2 mb-2 px-2">
                        <div className="w-1 h-6 bg-purple-500 rounded"></div>
                        <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">
                          Inside: {getElementName(item)}
                        </span>
                        <span className="text-xs text-purple-500">
                          ({item.powerClipContents.length} item{item.powerClipContents.length !== 1 ? 's' : ''})
                        </span>
                      </div>

                      {/* Nested Items */}
                      {item.powerClipContents.map((nestedItem, nestedIndex) => (
                        <div
                          key={nestedItem.id}
                          onClick={() => onElementSelect(nestedItem.id)}
                          className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ml-6 mb-1 ${
                            selectedElement === nestedItem.id
                              ? 'bg-purple-100 border-purple-400 shadow-md text-purple-900'
                              : 'bg-purple-50 border-purple-200 hover:bg-purple-100'
                          }`}
                        >
                          {/* Element Icon and Info */}
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="mr-3 flex-shrink-0">
                              {renderThumbnail(nestedItem)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium text-gray-900 truncate flex items-center">
                                  <span className="text-purple-500 mr-1">↳</span>
                                  {getElementName(nestedItem)}
                                </h3>
                                <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                                  <span className="text-xs text-gray-500">
                                    Nested
                                  </span>
                                  {/* Time info for video templates (nested items) - inline */}
                                  {isVideoTemplate && nestedItem.time && (
                                    <>
                                      <span className="text-xs text-gray-400">
                                        {formatTime(nestedItem.time.start)} - {formatTime((nestedItem.time.start || 0) + (nestedItem.time.duration || 0))}
                                      </span>
                                      {/* Green rectangle indicator for time range (nested) */}
                                      <div 
                                        className="h-3 w-8 bg-green-500 rounded-sm flex-shrink-0"
                                        title={`Time range: ${formatTime(nestedItem.time.start)} - ${formatTime((nestedItem.time.start || 0) + (nestedItem.time.duration || 0))}`}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                              {/* Preview text for nested text items */}
                              {nestedItem.type === 'text' && nestedItem.content && (
                                <div 
                                  className="text-sm text-gray-700 truncate mt-1"
                                  style={{
                                    fontFamily: nestedItem.fontFamily ? `"${nestedItem.fontFamily}", sans-serif` : 'inherit',
                                    fontSize: nestedItem.fontSize ? `${Math.min(nestedItem.fontSize, 14)}px` : 'inherit',
                                    fontWeight: nestedItem.fontWeight || 'normal',
                                    color: nestedItem.color || '#000000'
                                  }}
                                >
                                  {nestedItem.content}
                                </div>
                              )}
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-xs text-gray-500">
                                  {Math.round(nestedItem.x)}, {Math.round(nestedItem.y)}
                                </span>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs text-gray-500">
                                  {Math.round(nestedItem.width)} × {Math.round(nestedItem.height)}
                                </span>
                              </div>

                              {/* Category Field Info for Nested Items */}
                              {canAttachField(nestedItem) && (
                                <div className="mt-2">
                                  {getAttachedField(nestedItem) ? (
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded flex items-center">
                                        <Tag className="w-3 h-3 mr-1" />
                                        {getAttachedField(nestedItem).label}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeField(nestedItem.id);
                                        }}
                                        className="text-xs text-red-600 hover:text-red-800 p-1 rounded"
                                        title="Remove field"
                                      >
                                        <Unlink className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center space-x-1">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenAttachFieldDialog(nestedItem);
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded bg-blue-50 flex items-center"
                                        title="Attach field"
                                      >
                                        <Link className="w-3 h-3 mr-1" />
                                        Attach Field
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons for Nested Items */}
                          <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                            {/* Delete Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(nestedItem);
                              }}
                              className="p-1 hover:bg-red-100 rounded text-red-600"
                              title="Delete nested element"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer Info */}
      {sortedItems.length > 0 && (
        <div className="border-t border-gray-200 p-3 bg-gray-50 flex-shrink-0">
          <div className="text-xs text-gray-600 space-y-1">
            <div className="flex justify-between">
              <span>Total elements:</span>
              <span className="font-medium">{items.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Selected:</span>
              <span className="font-medium">
                {selectedElement ? sortedItems.find(item => item.id === selectedElement)?.type || 'None' : 'None'}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Layers range:</span>
              <span className="font-medium">
                {Math.min(...items.map(item => item.layer || 0))} - {Math.max(...items.map(item => item.layer || 0))}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirmDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Element</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete "<span className="font-medium text-gray-900">{deleteConfirmDialog.itemName}</span>"?
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Layer Item Tooltip */}
      {tooltipItem && (
        <div
          className="fixed z-[2000] bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-64 pointer-events-none"
          style={{
            left: Math.min(tooltipPosition.x, window.innerWidth - 280),
            top: Math.min(tooltipPosition.y, window.innerHeight - 300),
          }}
        >
          {/* Image preview for image/video items */}
          {(tooltipItem.type === 'image' || tooltipItem.type === 'video') && (tooltipItem.src || tooltipItem.imageUrl || tooltipItem.thumbnail || tooltipItem.poster) && (
            <div className="mb-2 rounded overflow-hidden bg-gray-100">
              <img
                src={tooltipItem.src || tooltipItem.imageUrl || tooltipItem.thumbnail || tooltipItem.poster}
                alt="preview"
                className="w-full h-32 object-contain"
              />
            </div>
          )}

          {/* Text content preview */}
          {tooltipItem.type === 'text' && tooltipItem.content && (
            <div className="mb-2 p-2 bg-gray-50 rounded text-sm text-gray-800 max-h-20 overflow-hidden" style={{ fontFamily: tooltipItem.fontFamily || 'inherit' }}>
              {tooltipItem.content.length > 200 ? tooltipItem.content.substring(0, 200) + '...' : tooltipItem.content}
            </div>
          )}

          {/* Properties */}
          <div className="space-y-1 text-xs text-gray-600">
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Type:</span>
              <span className="capitalize">{tooltipItem.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Size:</span>
              <span>{Math.round(tooltipItem.width || 0)} x {Math.round(tooltipItem.height || 0)}</span>
            </div>
            {tooltipItem.type === 'text' && tooltipItem.fontFamily && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Font:</span>
                <span className="truncate ml-2">{tooltipItem.fontFamily}</span>
              </div>
            )}
            {tooltipItem.type === 'text' && tooltipItem.fontSize && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Size:</span>
                <span>{tooltipItem.fontSize}px</span>
              </div>
            )}
            {tooltipItem.animation && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Animation:</span>
                <span className="capitalize">{tooltipItem.animation?.type || tooltipItem.animation}</span>
              </div>
            )}
            {tooltipItem.filters?.type && tooltipItem.filters.type !== 'none' && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Filter:</span>
                <span className="capitalize">{tooltipItem.filters.type}</span>
              </div>
            )}
            {tooltipItem.opacity !== undefined && tooltipItem.opacity !== 1 && (
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Opacity:</span>
                <span>{Math.round(tooltipItem.opacity * 100)}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Attach Field Dialog */}
      <AttachFieldDialog
        isOpen={attachFieldDialog.isOpen}
        onClose={handleCloseAttachFieldDialog}
        onAttachField={handleAttachFieldFromDialog}
        itemId={attachFieldDialog.itemId}
        itemType={attachFieldDialog.itemType}
        template_content={template_content}
        template_category_id={template_category_id}
        currentAttachedField={
          attachFieldDialog.itemId
            ? getAttachedField(items.find(item => item.id === attachFieldDialog.itemId) || {})
            : null
        }
      />
    </div>
  );
};

export default ItemsListSidebar;
